import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import {
  QrcodeErrorCallback,
  QrcodeResult,
  QrcodeSuccessCallback,
  QrDimensionFunction,
  QrDimensions
} from 'html5-qrcode/core'
import type { Html5QrcodeResult } from 'html5-qrcode'
import { isDev, Mtds, KeyboardIcon, useAuth, useSystemLog } from 'prove-common'
import {
  isValidCode,
  CodeTypes,
  inferFormatFromInput
} from '../utils/codeValidation'
import { pushMatomoEvent } from '../utils/matomoUtils'
import { ManualCodeEntryModal } from '../components/scan/ManualCodeEntryModal'
import Html5QrcodePlugin from '../components/html5-qrcode-plugin/Html5QrcodePlugin'

type UseCodeScanOptions = {
  type: CodeTypes | null
  onCodeScanned: (code: string, source: 'scan' | 'manual') => void
  context?: string
}

export const useCodeScan = ({
  type,
  onCodeScanned,
  context
}: UseCodeScanOptions) => {
  const { userId } = useAuth()
  const [searchParams] = useSearchParams()
  const logMutation = useSystemLog()
  const [modalOpen, setModalOpen] = useState(false)
  const [visitStart, setVisitStart] = useState<number | null>(null)

  useEffect(() => {
    setVisitStart(performance.now())
    return () => setVisitStart(null)
  }, [])

  const elapsed = () => (visitStart ? performance.now() - visitStart : 0)
  const isMobile = /android|iphone/i.test(navigator.userAgent)
  const manualEnabled =
    searchParams.get('manualEntry') === 'true' || !isMobile || isDev()

  const validateAndHandle = (
    result: QrcodeResult,
    source: 'scan' | 'manual'
  ) => {
    const resultText = result?.text
    if (!resultText) return
    if (isValidCode(result, type)) {
      onCodeScanned(resultText, source)
    } else {
      alert('Koden er ikke gyldig')
      if (type === 'skrottlapp') {
        pushMatomoEvent(
          'skrottlapp_scanner',
          source === 'scan' ? 'invalid_code_scanned' : 'invalid_code_manual'
        )
      }
      logMutation.mutate({
        type: 'ERROR',
        message: `ProveScan: Invalid code scanned ${JSON.stringify(result)} User id: ${userId} Elapsed: ${elapsed().toFixed(2)}ms Context: ${context ?? 'none'} ${globalThis.location} ${navigator.userAgent}`,
        timestamp: Date.now().toString()
      })
    }
  }

  const onScanSuccess: QrcodeSuccessCallback = (
    _decoded,
    res: Html5QrcodeResult
  ) => validateAndHandle(res.result, 'scan')

  const onScanFailure: QrcodeErrorCallback = (e) => {
    if (isDev()) console.log('onScanFailure', e)
  }

  const openManual = () => {
    logMutation.mutate({
      type: 'ERROR',
      message: `ProveScan: Manual code entry used; User id: ${userId} Elapsed: ${elapsed().toFixed(2)}ms Context: ${context ?? 'none'} ${globalThis.location} ${navigator.userAgent}`,
      timestamp: Date.now().toString()
    })
    setModalOpen(true)
  }

  const qrboxPercent = 0.7
  const qrbox: QrDimensionFunction = (w, h): QrDimensions => ({
    width: w * qrboxPercent,
    height: h * qrboxPercent
  })

  const manualEntry = (
    <>
      {manualEnabled && !modalOpen && (
        <Mtds.Button
          aria-label='Åpne vindu for å skrive inn kode manuelt'
          className='absolute bottom-4 right-4'
          data-cy='open-manual-entry'
          data-variant='primary'
          onClick={openManual}
        >
          <KeyboardIcon />
        </Mtds.Button>
      )}
      <ManualCodeEntryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCodeEntered={(code: string) => {
          const result: QrcodeResult = {
            text: code,
            format: inferFormatFromInput(code, type)
          }
          validateAndHandle(result, 'manual')
        }}
      />
    </>
  )

  const scanner = useMemo(
    () => (
      <Html5QrcodePlugin
        fps={10}
        qrbox={qrbox}
        qrCodeSuccessCallback={onScanSuccess}
        qrCodeErrorCallback={onScanFailure}
      />
    ),
    []
  )

  return {
    scanner,
    manualEntry
  }
}
